import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const NotifCtx = createContext();
export const useNotif = () => useContext(NotifCtx);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const audioRef = useRef(null);          // <audio> element (sempre existe)
  const audioCtxRef = useRef(null);       // AudioContext compartilhado (ding padrão)
  const unlockedRef = useRef(false);      // áudio já destravado?
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const [enabled, setEnabled] = useState(true);
  const [soundUrl, setSoundUrl] = useState(null);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");

  const isTarget = user && (user.role === "vendedor" || user.role === "producao");

  // Garante <audio> existe
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "auto";
    }
  }, []);

  // Atualiza src quando soundUrl muda
  useEffect(() => {
    if (!audioRef.current) return;
    if (soundUrl) {
      const url = soundUrl.startsWith("http") ? soundUrl : `${BACKEND}${soundUrl}`;
      audioRef.current.src = url;
      try { audioRef.current.load(); } catch {}
    } else {
      audioRef.current.removeAttribute("src");
    }
  }, [soundUrl]);

  // Destrava áudio no primeiro gesto do usuário (clique/tecla).
  // Isso é necessário por causa da política de autoplay dos navegadores.
  useEffect(() => {
    const unlock = async () => {
      if (unlockedRef.current) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
          if (audioCtxRef.current.state === "suspended") await audioCtxRef.current.resume();
        }
      } catch {}
      try {
        if (audioRef.current) {
          // Toca-pausa silencioso só para destravar o <audio>
          const prev = audioRef.current.volume;
          audioRef.current.volume = 0;
          const p = audioRef.current.play();
          if (p && p.catch) p.catch(() => {});
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.volume = prev || 1;
        }
      } catch {}
      unlockedRef.current = true;
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  // Sincroniza preferências do backend
  useEffect(() => {
    if (!user) return;
    setEnabled(user.notifications_enabled !== false);
    setSoundUrl(user.notification_sound_url || null);
  }, [user]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "denied";
    if (Notification.permission === "default") {
      try {
        const p = await Notification.requestPermission();
        setPermission(p);
        return p;
      } catch { return "denied"; }
    }
    setPermission(Notification.permission);
    return Notification.permission;
  }, []);

  const playDefaultDing = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }
      const now = ctx.currentTime;
      const tone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + start);
        gain.gain.linearRampToValueAtTime(0.3, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(now + start); osc.stop(now + start + dur + 0.05);
      };
      tone(880, 0, 0.2);
      tone(1320, 0.1, 0.26);
    } catch (e) { console.warn("[notif] default ding failed:", e); }
  }, []);

  const playSound = useCallback(() => {
    if (!enabled) return;
    if (soundUrl && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        const p = audioRef.current.play();
        if (p && p.catch) p.catch((err) => {
          console.warn("[notif] custom audio failed, falling back:", err);
          playDefaultDing();
        });
        return;
      } catch (e) {
        console.warn("[notif] custom audio exception:", e);
      }
    }
    playDefaultDing();
  }, [enabled, soundUrl, playDefaultDing]);

  const showBrowserNotification = useCallback((payload) => {
    if (!enabled) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      const title = `Novo Pedido Nº ${payload.order_number}`;
      const body = `${payload.product_summary || "Pedido"}${payload.customer_name ? ` — ${payload.customer_name}` : ""}\nStatus: ${payload.status || "-"}`;
      const n = new Notification(title, {
        body,
        icon: "/riosul-logo.png",
        badge: "/riosul-logo.png",
        tag: `sale-${payload.sale_id}`,
        renotify: true,
        requireInteraction: true, // mantém o pop-up no canto da tela até o usuário interagir
        silent: false,
      });
      n.onclick = () => {
        window.focus();
        if (payload.sale_id) window.location.href = `/vendas/${payload.sale_id}`;
        n.close();
      };
    } catch (e) {
      console.warn("[notif] browser notification failed:", e);
    }
  }, [enabled]);

  const handleIncoming = useCallback((payload) => {
    if (!enabled) return;
    playSound();
    showBrowserNotification(payload);
    toast.success(
      `Nº ${payload.order_number} — ${payload.product_summary}`,
      { description: `${payload.customer_name ? payload.customer_name + " • " : ""}${payload.status || ""}`, duration: 10000 }
    );
  }, [enabled, playSound, showBrowserNotification]);

  // Conecta o WebSocket
  useEffect(() => {
    if (!isTarget) return;
    const token = localStorage.getItem("access_token");
    if (!token) return;

    let closed = false;
    const connect = () => {
      if (closed) return;
      try {
        const wsBase = BACKEND.replace(/^http/, "ws");
        const ws = new WebSocket(`${wsBase}/api/ws/notifications?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onopen = () => {
          if (pingRef.current) clearInterval(pingRef.current);
          pingRef.current = setInterval(() => { try { ws.send("ping"); } catch {} }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === "new_sale") handleIncoming(data);
          } catch {}
        };
        ws.onclose = () => {
          if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
          if (!closed) {
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            reconnectRef.current = setTimeout(connect, 3000);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch (e) { console.warn("[notif] ws connect failed:", e); }
    };
    connect();
    return () => {
      closed = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [isTarget, handleIncoming]);

  return (
    <NotifCtx.Provider value={{
      enabled, setEnabled, soundUrl, setSoundUrl, permission, requestPermission,
      playSoundPreview: playSound, isTarget,
    }}>
      {children}
    </NotifCtx.Provider>
  );
}
